import { mapState } from 'vuex'


/**
 * Helper functions for question analysis
 * for patients
 * @mixin
 */
export default {
    computed: {
        ...mapState('provider', ['patientAnalytics']),
    },
    methods: {
        sentAndNotAnswered(row) {
            return row.event.length && row.event.find((questionnaire) => questionnaire.completed) === undefined
        },
        answered(row) {
            return row.event.find((questionnaire) => questionnaire.completed)
        },

        answeredCount(row) {
            return this.formatCount(row.event.filter((q) => q.completed).length, 'time')
        },
        lastAnswered(row) {
            if (row.event.length > 0)
                return row.event.reduce((answeredTime, q) => (q.completed && answeredTime < q.completed) ? q.completed : answeredTime, 0)
            return ''
        },
        lastRequest(row) {
            if (row.event.length > 0)
                return row.event[0].requested
            return ''
        },
        incomplete(row) {
            const incompleteCount = row.event.filter((questionnaire) => (! questionnaire.completed)).length
            return incompleteCount === 0 ? 0 : incompleteCount === 1 ? this.formatCount(incompleteCount, 'time') : this.formatCount(incompleteCount, 'times')
        },
        /**
         * Returns the array for question scores of all answered scored questions
         */
        questionScores(row, patientId) {
            try {
                if(row.content) {
                    const patientEventAnalytics = this.patientAnalytics[patientId] ? this.patientAnalytics[patientId] : null
                    let rowTotal = row.event.filter(event => event.completed)
                    const ifSury = Object.keys(rowTotal[0]).includes('survey')

                    if(ifSury) {
                        return rowTotal
                            .map((event) => {
                                let patientEventAnalyticsForRow = patientEventAnalytics && patientEventAnalytics[event.response || event.id] ? patientEventAnalytics[event.response || event.id] : undefined

                                let mainQuesObj = {
                                    completed: event.completed,
                                    score: patientEventAnalyticsForRow && 'score' in patientEventAnalyticsForRow ? patientEventAnalyticsForRow.score : undefined,
                                    provider_survey: event.provider_survey,
                                    computed_score: patientEventAnalyticsForRow && 'computed_score' in patientEventAnalyticsForRow ? patientEventAnalyticsForRow.computed_score : undefined,
                                    display_title: patientEventAnalyticsForRow && 'display_title' in patientEventAnalyticsForRow ? patientEventAnalyticsForRow.display_title : '',
                                    subQ: patientEventAnalyticsForRow && 'sub' in patientEventAnalyticsForRow ? patientEventAnalyticsForRow.sub : [],
                                }

                                return mainQuesObj
                            })
                            .sort((a, b) => b.completed - a.completed)
                    } else {
                        let retObjWithSubQ = rowTotal.flatMap((event) => {
                            let diffDateAns = patientEventAnalytics && patientEventAnalytics[event.response || event.id] ? patientEventAnalytics[event.response || event.id] : undefined
                            if(diffDateAns && diffDateAns.sub && Object.keys(diffDateAns.sub).length > 0) return diffDateAns.sub.questionnaire
                            else return []
                        })

                        let finalRes

                        if(retObjWithSubQ.length > 0 && retObjWithSubQ.some((obj) => obj.hasOwnProperty('score'))) {
                            finalRes = retObjWithSubQ.filter((obj) => obj.hasOwnProperty('score'))
                            .reduce((acc, curr) => {
                                if(acc[curr.display_title]) acc[curr.display_title].push(curr)
                                else acc[curr.display_title] = [curr]
                                return acc
                            },{})

                            for(const subq in finalRes) {
                                finalRes[subq] = finalRes[subq].sort((a,b) => b.completed - a.completed)
                            }
                        } else {
                            finalRes = rowTotal
                            .map((event) => {
                                let patientEventAnalyticsForRow = patientEventAnalytics && patientEventAnalytics[event.response || event.id] ? patientEventAnalytics[event.response || event.id] : undefined

                                return {
                                    completed: event.completed,
                                    score: patientEventAnalyticsForRow && 'score' in patientEventAnalyticsForRow ? patientEventAnalyticsForRow.score : undefined,
                                    provider_survey: event.provider_survey,
                                    computed_score: patientEventAnalyticsForRow && 'computed_score' in patientEventAnalyticsForRow ? patientEventAnalyticsForRow.computed_score : undefined,
                                    display_title: patientEventAnalyticsForRow && 'display_title' in patientEventAnalyticsForRow ? patientEventAnalyticsForRow.display_title : '',
                                }
                            })
                            .sort((a, b) => b.completed - a.completed)
                            .reduce((acc, curr) => {
                                if(acc[curr.display_title]) acc[curr.display_title].push(curr)
                                else acc[curr.display_title] = [curr]
                                return acc
                            },{})
                        }
                        // console.log('final: ', finalRes)
                        return finalRes
                    }
                }
                return []
            } catch (error) {
                console.error('Problem calculating score', error)
                return []
            }
            
        },
        /**
         * Returns score for a single answer to a question
         */
        getScore(answer,question) {
            try {
                // New `long_text_bubble` type support
                if(question.type === 'long_text_bubble') {
                    return answer.answer.reduce((qScore, ansObj) => {
                        const questObj = question.options.find((quesObj) => quesObj.id === ansObj.id)
                        return qScore + (questObj ?  (questObj.score || 0) : 0)
                    }, 0)
                }

                if(!question.score)
                    return 0

                // Return 0 if `default` score is empty
                
                if(Object.keys(question.score.default).length === 0) {
                    return 0
                }

                // Special case for `binary` type question where answer key is alwasy `0` with value as `YES` or `NO`
                if(question.type === 'binary')
                    return answer.answer[0] === 'Yes' ? question.score.default[0] : question.score.default[1]
                
                // Special case for 'radio' type question where answer has `value` where score is in key of value -1
                else if(question.type === 'radio')
                    return question.score.default[answer.value - 1]
                
                // For questions with arrays for answer, gets the index of the answers in `option` key summ up respective score and returns
                else if(Array.isArray(answer.answer)) {
                    return answer.answer.reduce((qScore, answer) => {
                        if(!question.option)
                            return 0
                        const optionIndex = question.option.indexOf(answer)
                        if(optionIndex == -1)
                            return qScore
                        return qScore + question.score.default[optionIndex]
                    }, 0)
                }

                // For all other questions with answers as objects with keys responding to keys in the score.default
                else {
                    return Object.keys(answer.answer).reduce((qScore, ansKey) => qScore + question.score.default[ansKey] || 0, 0)
                }
            } catch (error) {
                console.error(error)
                return 0
            }
            
        },
        incompleteLink(row) {
            if (row.event.length > 0) {
                const lastReqEvent = row.event.find((currEvent) => !currEvent.completed)
                if(lastReqEvent)
                    return lastReqEvent.url
            }
            return row.event[0].url
        },
        /**
         * Gets string of answered date of questionnnaire depending upon first answered question
         * @param {Object} row 
         */
        getAnsweredDate(row) {
            try {
                if(row.event && row.event.length > 0 && row.content) {
                    // if answered and type is 'date'
                    if(row.event.some((rEvent) => rEvent.completed) && row.content.question.length > 0 && row.content.question[0].type === 'date') {
                        const firstQuestionId = row.content.question[0].id
                        const dateAnswerList = row.event.map((questEvent) => {
                            const dateQuestion = (questEvent.question && questEvent.completed) ? questEvent.question.find(({ id }) => firstQuestionId === id) : null
                            if(dateQuestion && dateQuestion.answer && Array.isArray(dateQuestion.answer) && dateQuestion.answer.length > 0) {
                                const dateAnswer = dateQuestion.answer[0]
                                if (Object.keys(dateAnswer).length)
                                    return new Date(dateAnswer.year, dateAnswer.month - 1, dateAnswer.date)
                            }
                        })
                        .filter((date) => date)
                        .sort((dateA, dateB) => dateB - dateA)
                        .map((date) => this.formatDate(date))

                        if(dateAnswerList.length > 0)
                            return ' &bull; ' + dateAnswerList.join(' &bull; ')
                    }
                }
                return null
            } catch (error) {
                console.error('Problem getting date', error)
                return null
            }
        },
        isAsked(row) {
            try {
                if(row.event && row.event.length > 0)
                    return true
                return false
            } catch (error) {
                console.error('Problem calculating isAsked', error)
                return false
            }
        },
        getRowClass(scope) {
            const answered = (this.answered(scope.row) && this.incomplete(scope.row) === 0) ? 'answered ' : ''
            const notYet = this.isAsked(scope.row) ? '' : 'not-yet '
            return this.isSurvey ? ('survey ' + notYet + answered) : '' 
        },
        getCellClass({columnIndex}) {
            return `questionnaire-cell ${this.isSurvey ? 'survey-cell' : ''} ${ (columnIndex === 0) ? 'first' : ''}`
        },
        findIfQuestion(row) {
            return row.event.length && row.event.find((questionnaire) => questionnaire.hasOwnProperty('provider_survey')) === undefined
        },
        findIfSurvey(row, patientId) {
            try {
                if(row.content) {
                    let rowTotal = row.event.filter(event => event.completed)
                    return Object.keys(rowTotal[0]).includes('survey')
                }
            }  catch (error) {
                console.error('Problem calculating score', error)
                return false
            }
        }
    }
}
